import { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { useOutletContext } from "react-router-dom";
import {
  listWorkStages,
  createWorkStage,
  updateWorkStage,
  deleteWorkStage,
} from "../../../../services/workStages";
import { ensurePermission } from "../../../../utils/permissions";
import type { WorkStageItem } from "../../../../services/workStages";
import { jobTypes as jobTypesApi } from "../../../../services/admin";
import RIcon from "../../../../components/RIcon";

interface JobTypeOption {
  id: number;
  name: string;
}

interface StageFormData {
  name: string;
  job_type_id: string;
  stage_order: string;
  description: string;
}

const EMPTY_FORM: StageFormData = {
  name: "",
  job_type_id: "",
  stage_order: "0",
  description: "",
};

export default function JobStagesPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [stages, setStages] = useState<WorkStageItem[]>([]);
  const [jobTypes, setJobTypes] = useState<JobTypeOption[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterJobType, setFilterJobType] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<StageFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<StageFormData>>({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadStages = useCallback(async (q?: string, jtId?: number) => {
    try {
      setPageLoading(true);
      const data = await listWorkStages(q || undefined, jtId || undefined);
      setStages(data);
    } catch (err) {
      showToast("加载阶段列表失败");
      console.error("Failed to load work stages:", err);
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStages();
    jobTypesApi.list().then(setJobTypes).catch(() => {});
  }, [loadStages]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadStages(search.trim(), filterJobType ? Number(filterJobType) : undefined);
  };

  const handleSearchClear = () => {
    setSearch("");
    setFilterJobType("");
    loadStages();
  };

  const handleFilterChange = (jtId: string) => {
    setFilterJobType(jtId);
    loadStages(search.trim(), jtId ? Number(jtId) : undefined);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (stage: WorkStageItem) => {
    setEditingId(stage.id);
    setForm({
      name: stage.name,
      job_type_id: stage.job_type_id ? String(stage.job_type_id) : "",
      stage_order: String(stage.stage_order ?? 0),
      description: stage.description ?? "",
    });
    setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Partial<StageFormData> = {};
    if (!form.name.trim()) errs.name = "请输入阶段名称";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "work_stages:write", showToast)) return;
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        job_type_id: form.job_type_id ? Number(form.job_type_id) : undefined,
        stage_order: form.stage_order ? Number(form.stage_order) : 0,
        description: form.description.trim() || undefined,
      };
      if (editingId !== null) {
        await updateWorkStage(editingId, payload);
        showToast("阶段已更新");
      } else {
        await createWorkStage(payload);
        showToast("阶段已创建");
      }
      closeModal();
      await loadStages(
        search.trim() || undefined,
        filterJobType ? Number(filterJobType) : undefined
      );
    } catch (err) {
      showToast(editingId !== null ? "更新失败，请重试" : "创建失败，请重试");
      console.error("Failed to save work stage:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ensurePermission(user, "work_stages:delete", showToast)) return;
    if (deleteId === null) return;
    try {
      await deleteWorkStage(deleteId);
      setDeleteId(null);
      showToast("阶段已删除");
      await loadStages(
        search.trim() || undefined,
        filterJobType ? Number(filterJobType) : undefined
      );
    } catch (err) {
      showToast("删除失败，请重试");
      console.error("Failed to delete work stage:", err);
    }
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 whitespace-nowrap">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-[#1A2B3C]">研发阶段管理</h1>
          <p className="text-sm text-[#718096] mt-0.5">定义各工种在项目实施中的不同阶段</p>
        </div>
        <button
          onClick={() => ensurePermission(user, "work_stages:write", showToast) && openCreate()}
          className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
        >
          <RIcon name="add-line" />新增阶段
        </button>
      </div>

      {/* Search + Filter */}
      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <RIcon name="search-line" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096] text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索阶段名称..."
            className="w-full h-9 pl-8 pr-8 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#0EA5A0] text-[#1A2B3C]"
          />
          {search && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#718096] hover:text-[#1A2B3C] cursor-pointer"
            >
              <RIcon name="close-line" className="text-sm" />
            </button>
          )}
        </div>
        <Select
          showSearch
          allowClear
          placeholder="全部工种"
          style={{ width: 180 }}
          value={filterJobType || undefined}
          onChange={(v) => handleFilterChange((v ?? "") as string)}
          optionFilterProp="label"
          options={jobTypes.map((jt) => ({ value: String(jt.id), label: jt.name }))}
        />
        <button
          type="submit"
          className="h-9 px-4 bg-[#F4F8FB] hover:bg-gray-100 text-[#4A5568] text-sm rounded-lg cursor-pointer whitespace-nowrap border border-gray-200"
        >
          搜索
        </button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["阶段名称", "所属工种", "排序", "描述", "操作"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-[#718096]">
                    <RIcon name="loader-4-line" className="animate-spin text-lg mr-2" />加载中...
                  </td>
                </tr>
              ) : stages.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-[#718096]">
                    暂无阶段数据
                  </td>
                </tr>
              ) : (
                stages.map((stage) => (
                  <tr key={stage.id} className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-[#1A2B3C] whitespace-nowrap">{stage.name}</td>
                    <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">
                      {stage.job_type_name ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 text-sky-700 rounded text-xs">
                          <RIcon name="tools-line" className="text-xs" />{stage.job_type_name}
                        </span>
                      ) : (
                        <span className="text-[#718096]">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#4A5568] whitespace-nowrap">{stage.stage_order}</td>
                    <td className="px-4 py-3 text-[#4A5568] max-w-[300px] truncate">{stage.description || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => ensurePermission(user, "work_stages:write", showToast) && openEdit(stage)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] cursor-pointer"
                        >
                          <RIcon name="edit-line" className="text-sm" />
                        </button>
                        <button
                          onClick={() => ensurePermission(user, "work_stages:delete", showToast) && setDeleteId(stage.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer"
                        >
                          <RIcon name="delete-bin-line" className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">
                {editingId !== null ? "编辑阶段" : "新增阶段"}
              </h3>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Stage Name */}
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                  阶段名称 <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  placeholder="如：设计阶段、施工阶段、验收阶段"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={`w-full border rounded-lg px-3 h-10 text-sm outline-none transition-colors ${
                    errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"
                  }`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Job Type */}
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">所属工种</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="不限工种"
                  style={{ width: "100%" }}
                  value={form.job_type_id || undefined}
                  onChange={(v) => setForm({ ...form, job_type_id: (v ?? "") as string })}
                  optionFilterProp="label"
                  options={jobTypes.map((jt) => ({ value: String(jt.id), label: jt.name }))}
                />
              </div>

              {/* Stage Order */}
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">排序</label>
                <input
                  type="number"
                  placeholder="数字越小越靠前"
                  value={form.stage_order}
                  onChange={(e) => setForm({ ...form, stage_order: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">描述</label>
                <textarea
                  placeholder="描述该阶段的工作内容和要求"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] resize-none"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {saving ? (
                    <><RIcon name="loader-4-line" className="animate-spin" />保存中...</>
                  ) : (
                    editingId !== null ? "保存更改" : "创建阶段"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RIcon name="delete-bin-line" className="text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">确认删除阶段</h3>
            <p className="text-sm text-[#718096] mb-6">删除后该阶段数据将无法恢复，请谨慎操作</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 h-10 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
